import React, { useEffect, useMemo } from "react";

function DebtDetailModal({ vehicleNumber, rows, onClose }) {
  // Đóng modal khi nhấn ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const detailRows = useMemo(() => {
    if (!rows || !vehicleNumber) return [];

    const vehicleRows = rows.filter((row) => {
      const soXe = row.soXe || "Chưa có số xe";
      return soXe === vehicleNumber;
    });

    return vehicleRows
      .map((row) => {
        const doanhThu =
          typeof row.doanhThu === "number"
            ? row.doanhThu
            : Number(String(row.doanhThu || "").replace(/[^\d-]/g, "")) || 0;

        const keToan =
          row.keToanChuyen === "" ||
          row.keToanChuyen === null ||
          row.keToanChuyen === undefined
            ? 0
            : typeof row.keToanChuyen === "number"
            ? row.keToanChuyen
            : Number(String(row.keToanChuyen).replace(/[^\d-]/g, "")) || 0;

        const conNo = doanhThu - keToan; // Có thể âm (thừa) hoặc dương (nợ)

        return {
          ngay: row.ngay || "—",
          laiXe: row.laiXe || "—",
          dieuPhoi: row.dieuPhoi || "—",
          doanhThu,
          keToan,
          conNo,
          noteNhap: row.noteNhap || "",
        };
      })
      .sort((a, b) => {
        if (!a.ngay || a.ngay === "—") return 1;
        if (!b.ngay || b.ngay === "—") return -1;
        try {
          const [d1, m1, y1] = a.ngay.split("/").map(Number);
          const [d2, m2, y2] = b.ngay.split("/").map(Number);
          const date1 = new Date(y1, m1 - 1, d1);
          const date2 = new Date(y2, m2 - 1, d2);
          return date2 - date1;
        } catch {
          return a.ngay.localeCompare(b.ngay, "vi");
        }
      });
  }, [rows, vehicleNumber]);

  const totalDoanhThu = detailRows.reduce((sum, r) => sum + r.doanhThu, 0);
  const totalKeToan = detailRows.reduce((sum, r) => sum + r.keToan, 0);
  const totalConNo = totalDoanhThu - totalKeToan; // Có thể âm (thừa) hoặc dương (nợ)

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
              Chi tiết công nợ - Xe {vehicleNumber}
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Tổng: {totalDoanhThu.toLocaleString("vi-VN")} đ - Đã chuyển:{" "}
              {totalKeToan.toLocaleString("vi-VN")} đ -{" "}
              {totalConNo > 0
                ? `Nợ: ${Math.abs(totalConNo).toLocaleString("vi-VN")} đ`
                : totalConNo < 0
                ? `Thừa: ${Math.abs(totalConNo).toLocaleString("vi-VN")} đ`
                : "Khớp"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-slate-600 hover:bg-slate-100"
            aria-label="Đóng"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-xs sm:text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Ngày
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Lái xe
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Điều phối
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Doanh thu
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Đã chuyển
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Nợ/Thừa
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Ghi chú
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {detailRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-2 sm:px-3 py-4 text-center text-xs text-slate-500"
                    >
                      Chưa có dữ liệu cho xe này.
                    </td>
                  </tr>
                ) : (
                  detailRows.map((row, idx) => {
                    const isBigDebt = row.conNo >= 2000000;
                    const isThua = row.conNo < 0;
                    const isNo = row.conNo > 0;
                    const hasDoanhThu = row.doanhThu && row.doanhThu > 0;
                    const hasKeToan = row.keToan > 0;
                    const needsAttention = hasDoanhThu && !hasKeToan;
                    return (
                      <tr key={idx} className={`${isBigDebt ? "bg-red-50" : needsAttention ? "bg-amber-50" : "bg-white"} hover:bg-slate-100`}>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                          {row.ngay}
                        </td>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                          {row.laiXe}
                        </td>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                          {row.dieuPhoi}
                        </td>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-right text-slate-700">
                          {row.doanhThu.toLocaleString("vi-VN")} đ
                        </td>
                        <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-right text-slate-700">
                          {row.keToan > 0
                            ? row.keToan.toLocaleString("vi-VN") + " đ"
                            : "—"}
                        </td>
                        <td
                          className={`px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-right font-semibold ${
                            isBigDebt
                              ? "text-red-700"
                              : isNo
                              ? "text-amber-700"
                              : isThua
                              ? "text-blue-700"
                              : "text-emerald-700"
                          }`}
                        >
                          {isNo
                            ? `Nợ ${Math.abs(row.conNo).toLocaleString("vi-VN")} đ`
                            : isThua
                            ? `Thừa ${Math.abs(row.conNo).toLocaleString("vi-VN")} đ`
                            : "0 đ"}
                        </td>
                        <td
                          className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700 max-w-[150px] truncate"
                          title={row.noteNhap}
                        >
                          {row.noteNhap || "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DebtDetailModal;

